/**
 * ChatWindow Component
 *
 * Message display area for chat channels and DMs with:
 * - Message list with virtual scrolling support
 * - Auto-scroll to bottom on new messages
 * - Infinite scroll for loading message history
 * - Loading states and empty state handling
 * - Date separators for message grouping
 *
 * @module components/chat/ChatWindow
 */

import { useRef, useEffect, useCallback, useState, useMemo, memo } from 'react';
import {
  Loader2,
  MessageSquare,
  ArrowDown,
  ChevronUp,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { useChat } from '../../contexts/ChatContext.jsx';
import { MessageContent, MessageAttachments } from './ChatMessage.jsx';
import { Avatar } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * Scroll threshold for showing "scroll to bottom" button (in pixels)
 */
const SCROLL_THRESHOLD = 200;

/**
 * Threshold for triggering infinite scroll (distance from top in pixels)
 */
const INFINITE_SCROLL_THRESHOLD = 100;

/**
 * Debounce delay for scroll handlers (in milliseconds)
 */
const SCROLL_DEBOUNCE_MS = 100;

/**
 * Animation duration for smooth scroll (in milliseconds)
 */
const SCROLL_ANIMATION_DURATION = 300;

// ============================================
// Helper Functions
// ============================================

/**
 * Format date for message separators
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
function formatDateSeparator(date) {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time for comparison
  const messageDateOnly = new Date(messageDate.toDateString());
  const todayOnly = new Date(today.toDateString());
  const yesterdayOnly = new Date(yesterday.toDateString());

  if (messageDateOnly.getTime() === todayOnly.getTime()) {
    return 'Today';
  }
  if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Yesterday';
  }

  // Format as "Month Day, Year"
  return messageDate.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format time for messages
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted time string
 */
function formatMessageTime(date) {
  return new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Group messages by date
 * @param {Array} messages - Array of messages
 * @returns {Array} Array of {date, messages} groups
 */
function groupMessagesByDate(messages) {
  const groups = [];
  let currentDate = null;
  let currentGroup = null;

  messages.forEach((message) => {
    const messageDate = new Date(message.createdAt).toDateString();

    if (messageDate !== currentDate) {
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentDate = messageDate;
      currentGroup = {
        date: message.createdAt,
        messages: [message]
      };
    } else {
      currentGroup.messages.push(message);
    }
  });

  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

/**
 * Check if two messages are from the same sender within a time window
 * @param {object} prevMessage - Previous message
 * @param {object} message - Current message
 * @param {number} [windowMs=300000] - Time window in milliseconds (default: 5 minutes)
 * @returns {boolean} Whether messages can be grouped
 */
function canGroupMessages(prevMessage, message, windowMs = 300000) {
  if (!prevMessage) return false;
  if (prevMessage.senderId !== message.senderId) return false;
  if (prevMessage.senderType !== message.senderType) return false;

  const prevTime = new Date(prevMessage.createdAt).getTime();
  const currTime = new Date(message.createdAt).getTime();

  return currTime - prevTime < windowMs;
}

// ============================================
// Sub-Components
// ============================================

/**
 * Date separator between message groups
 * @param {object} props - Component props
 * @param {string} props.date - Date string
 * @returns {React.ReactElement} Date separator
 */
const DateSeparator = memo(function DateSeparator({ date }) {
  return (
    <div className="flex items-center justify-center my-4" role="separator">
      <div className="flex-1 border-t border-gray-700" />
      <span className="px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
        {formatDateSeparator(date)}
      </span>
      <div className="flex-1 border-t border-gray-700" />
    </div>
  );
});

/**
 * Simple message display (used when ChatMessage component is not yet loaded)
 * @param {object} props - Component props
 * @param {object} props.message - Message object
 * @param {boolean} props.isGrouped - Whether message is grouped with previous
 * @param {boolean} props.isOwn - Whether message is from current user
 * @returns {React.ReactElement} Message item
 */
const MessageItem = memo(function MessageItem({ message, isGrouped, isOwn }) {
  const time = formatMessageTime(message.createdAt);

  return (
    <div
      className={`
        group px-4 py-1 hover:bg-gray-800/30 transition-colors
        ${isGrouped ? 'py-0.5' : 'pt-2'}
      `}
      data-message-id={message.id}
    >
      <div className="flex items-start gap-3 max-w-3xl">
        {/* Avatar - only show for first message in group */}
        {!isGrouped && (
          <Avatar
            name={message.senderName}
            type={message.senderType || 'user'}
            avatarUrl={message.senderAvatarUrl}
            size="md"
          />
        )}

        {/* Message content */}
        <div className={`flex-1 min-w-0 ${isGrouped ? 'ml-11' : ''}`}>
          {/* Header - only show for first message in group */}
          {!isGrouped && (
            <div className="flex items-baseline gap-2 mb-0.5">
              <span
                className={`
                  text-sm font-semibold
                  ${isOwn ? 'text-emerald-400' : 'text-gray-200'}
                `}
              >
                {message.senderName || 'Unknown'}
              </span>
              <span className="text-xs text-gray-500">{time}</span>
            </div>
          )}

          {/* Content - rendered with Markdown support */}
          <MessageContent
            content={message.content}
            mentionedUserIds={message.mentionedUserIds}
            mentionedAgentIds={message.mentionedAgentIds}
          />

          {/* Attachments */}
          <MessageAttachments attachments={message.attachments} />

          {/* Reply indicator */}
          {message.replyToId && (
            <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
              <span className="italic">Replying to a message</span>
            </div>
          )}
        </div>

        {/* Timestamp on hover for grouped messages */}
        {isGrouped && (
          <span className="text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {time}
          </span>
        )}
      </div>
    </div>
  );
});

/**
 * Loading indicator for message loading
 * @param {object} props - Component props
 * @param {string} [props.text] - Loading text
 * @returns {React.ReactElement} Loading indicator
 */
function LoadingIndicator({ text = 'Loading messages...' }) {
  return (
    <div className="flex items-center justify-center py-4">
      <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mr-2" />
      <span className="text-sm text-gray-400">{text}</span>
    </div>
  );
}

/**
 * Loading skeleton for initial load
 * @returns {React.ReactElement} Loading skeleton
 */
function LoadingSkeleton() {
  return (
    <div className="flex-1 p-4 space-y-4" aria-busy="true" aria-label="Loading messages">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-start gap-3 animate-pulse">
          <div className="w-8 h-8 bg-gray-700 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-24 bg-gray-700 rounded" />
              <div className="h-3 w-16 bg-gray-700 rounded" />
            </div>
            <div className="h-4 w-full bg-gray-700 rounded" />
            <div className="h-4 w-3/4 bg-gray-700 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no messages exist
 * @param {object} props - Component props
 * @param {boolean} props.isChannel - Whether this is a channel or DM
 * @param {string} [props.channelName] - Channel name
 * @returns {React.ReactElement} Empty state
 */
function EmptyState({ isChannel, channelName }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
        <MessageSquare className="w-8 h-8 text-gray-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-2">
        {isChannel
          ? `Welcome to #${channelName || 'this channel'}!`
          : 'Start a conversation'}
      </h3>
      <p className="text-sm text-gray-500 max-w-sm">
        {isChannel
          ? 'This is the beginning of the channel. Send a message to get started.'
          : 'Send a direct message to start chatting.'}
      </p>
    </div>
  );
}

/**
 * Error state when message loading fails
 * @param {object} props - Component props
 * @param {string} props.error - Error message
 * @param {Function} props.onRetry - Retry callback
 * @returns {React.ReactElement} Error state
 */
function ErrorState({ error, onRetry }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-red-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-300 mb-2">
        Failed to load messages
      </h3>
      <p className="text-sm text-gray-500 max-w-sm mb-4">
        {error || 'An error occurred while loading messages.'}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Load more indicator at the top
 * @param {object} props - Component props
 * @param {boolean} props.isLoading - Whether loading is in progress
 * @param {boolean} props.hasMore - Whether there are more messages to load
 * @param {Function} props.onLoadMore - Load more callback
 * @returns {React.ReactElement|null} Load more indicator or null
 */
function LoadMoreIndicator({ isLoading, hasMore, onLoadMore }) {
  if (!hasMore && !isLoading) return null;

  return (
    <div className="flex items-center justify-center py-4">
      {isLoading ? (
        <LoadingIndicator text="Loading older messages..." />
      ) : (
        <button
          type="button"
          onClick={onLoadMore}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors"
        >
          <ChevronUp className="w-4 h-4" />
          Load older messages
        </button>
      )}
    </div>
  );
}

/**
 * Scroll to bottom button
 * @param {object} props - Component props
 * @param {Function} props.onClick - Click handler
 * @param {number} [props.newMessageCount] - Number of new messages
 * @returns {React.ReactElement} Scroll button
 */
function ScrollToBottomButton({ onClick, newMessageCount = 0 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all animate-fade-in"
      aria-label="Scroll to bottom"
    >
      <ArrowDown className="w-4 h-4" />
      {newMessageCount > 0 && (
        <span className="bg-white text-indigo-600 text-xs font-bold px-1.5 py-0.5 rounded-full">
          {newMessageCount > 99 ? '99+' : newMessageCount}
        </span>
      )}
    </button>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * ChatWindow Component
 *
 * Displays messages with infinite scroll support and auto-scroll to bottom.
 *
 * @param {object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.renderMessage] - Custom message renderer
 * @param {Function} [props.onMessageClick] - Message click handler
 * @param {string} [props.currentUserId] - Current user ID for "own" message styling
 * @returns {React.ReactElement} Chat window
 */
export default function ChatWindow({
  className = '',
  renderMessage,
  onMessageClick,
  currentUserId
}) {
  // ----------------------------------------
  // Context
  // ----------------------------------------

  const {
    messages,
    messagesLoading,
    messagesHasMore,
    activeChannel,
    activeDmRecipient,
    fetchMessages,
    fetchDmMessages
  } = useChat();

  // ----------------------------------------
  // Refs
  // ----------------------------------------

  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const isUserScrolled = useRef(false);
  const lastScrollTop = useRef(0);
  const scrollDebounceTimer = useRef(null);
  const prevMessagesLength = useRef(0);

  // ----------------------------------------
  // State
  // ----------------------------------------

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  const isChannel = Boolean(activeChannel);
  const isDm = Boolean(activeDmRecipient);
  const hasSelection = isChannel || isDm;

  // Debug: Log active channel and messages for channel-bug investigation
  useEffect(() => {
    console.log('[ChatWindow] Render state:', {
      activeChannelId: activeChannel?.id,
      activeChannelName: activeChannel?.displayName || activeChannel?.name,
      messagesCount: messages?.length || 0,
      firstMessageChannelId: messages?.[0]?.channelId,
      messagesLoading
    });
  }, [activeChannel, messages, messagesLoading]);

  const channelName = activeChannel?.displayName || activeChannel?.name;
  const recipientName = activeDmRecipient?.displayName || activeDmRecipient?.name;

  /**
   * Messages grouped by date
   */
  const messageGroups = useMemo(() => {
    if (!messages || messages.length === 0) return [];
    return groupMessagesByDate(messages);
  }, [messages]);

  // ----------------------------------------
  // Scroll Handlers
  // ----------------------------------------

  /**
   * Scroll to the bottom of the message list
   * @param {boolean} [smooth=true] - Whether to use smooth scrolling
   */
  const scrollToBottom = useCallback((smooth = true) => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
      isUserScrolled.current = false;
      setShowScrollButton(false);
      setNewMessageCount(0);
    }
  }, []);

  /**
   * Handle scroll events with debouncing
   */
  const handleScroll = useCallback(() => {
    if (scrollDebounceTimer.current) {
      clearTimeout(scrollDebounceTimer.current);
    }

    scrollDebounceTimer.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Determine scroll direction
      const isScrollingUp = scrollTop < lastScrollTop.current;
      lastScrollTop.current = scrollTop;

      // Update user scroll state - reset when at bottom, set when scrolling up
      if (distanceFromBottom <= SCROLL_THRESHOLD) {
        isUserScrolled.current = false;
      } else if (isScrollingUp) {
        isUserScrolled.current = true;
      }

      // Show/hide scroll to bottom button
      setShowScrollButton(distanceFromBottom > SCROLL_THRESHOLD);

      // Infinite scroll - load more when near top
      if (scrollTop < INFINITE_SCROLL_THRESHOLD && messagesHasMore && !isLoadingMore) {
        handleLoadMore();
      }
    }, SCROLL_DEBOUNCE_MS);
  }, [messagesHasMore, isLoadingMore]);

  // ----------------------------------------
  // Load More Handler
  // ----------------------------------------

  /**
   * Load older messages (infinite scroll)
   */
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !messagesHasMore) return;

    setIsLoadingMore(true);
    setError(null);

    try {
      const oldestMessage = messages[0];
      const cursor = oldestMessage?.id;

      if (activeChannel) {
        await fetchMessages(activeChannel.id, { before: cursor });
      } else if (activeDmRecipient) {
        await fetchDmMessages(activeDmRecipient.id, { before: cursor });
      }
    } catch (err) {
      setError('Failed to load older messages');
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    messagesHasMore,
    messages,
    activeChannel,
    activeDmRecipient,
    fetchMessages,
    fetchDmMessages
  ]);

  /**
   * Retry loading messages after error
   */
  const handleRetry = useCallback(() => {
    setError(null);
    if (activeChannel) {
      fetchMessages(activeChannel.id);
    } else if (activeDmRecipient) {
      fetchDmMessages(activeDmRecipient.id);
    }
  }, [activeChannel, activeDmRecipient, fetchMessages, fetchDmMessages]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  /**
   * Auto-scroll to bottom when new messages arrive
   * (if user hasn't scrolled up manually)
   */
  useEffect(() => {
    const currentLength = messages?.length || 0;
    const prevLength = prevMessagesLength.current;

    if (currentLength > prevLength) {
      // New messages arrived
      const newCount = currentLength - prevLength;

      if (!isUserScrolled.current) {
        // Auto-scroll if user hasn't scrolled up
        scrollToBottom(true);
      } else {
        // Update new message count indicator
        setNewMessageCount((prev) => prev + newCount);
      }
    }

    prevMessagesLength.current = currentLength;
  }, [messages, scrollToBottom]);

  /**
   * Scroll to bottom on initial load
   */
  useEffect(() => {
    if (messages?.length > 0 && !messagesLoading && !isLoadingMore) {
      // Reset scroll state on channel/conversation change
      isUserScrolled.current = false;
      setNewMessageCount(0);
      scrollToBottom(false);
    }
  }, [activeChannel?.id, activeDmRecipient?.id]);

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (scrollDebounceTimer.current) {
        clearTimeout(scrollDebounceTimer.current);
      }
    };
  }, []);

  /**
   * Reset error state on channel/conversation change
   */
  useEffect(() => {
    setError(null);
  }, [activeChannel?.id, activeDmRecipient?.id]);

  // ----------------------------------------
  // Render - No Selection
  // ----------------------------------------

  if (!hasSelection) {
    return (
      <div
        className={`
          flex flex-col flex-1 items-center justify-center
          bg-gray-900 text-gray-500
          ${className}
        `}
      >
        <MessageSquare className="w-12 h-12 mb-4 opacity-30" />
        <p className="text-sm">Select a channel or conversation to start chatting</p>
      </div>
    );
  }

  // ----------------------------------------
  // Render - Loading State
  // ----------------------------------------

  if (messagesLoading && messages.length === 0) {
    return (
      <div className={`flex flex-col flex-1 bg-gray-900 ${className}`}>
        <LoadingSkeleton />
      </div>
    );
  }

  // ----------------------------------------
  // Render - Error State
  // ----------------------------------------

  if (error && messages.length === 0) {
    return (
      <div className={`flex flex-col flex-1 bg-gray-900 ${className}`}>
        <ErrorState error={error} onRetry={handleRetry} />
      </div>
    );
  }

  // ----------------------------------------
  // Render - Empty State
  // ----------------------------------------

  if (!messagesLoading && messages.length === 0) {
    return (
      <div className={`flex flex-col flex-1 bg-gray-900 ${className}`}>
        <EmptyState
          isChannel={isChannel}
          channelName={channelName}
        />
      </div>
    );
  }

  // ----------------------------------------
  // Render - Message List
  // ----------------------------------------

  return (
    <div
      className={`
        flex flex-col flex-1 relative
        bg-gray-900 overflow-hidden
        ${className}
      `}
    >
      {/* Scrollable message container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto scroll-smooth"
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label={isChannel ? `Messages in ${channelName}` : `Conversation with ${recipientName}`}
      >
        {/* Load more indicator */}
        <LoadMoreIndicator
          isLoading={isLoadingMore}
          hasMore={messagesHasMore}
          onLoadMore={handleLoadMore}
        />

        {/* Message groups */}
        {messageGroups.map((group, groupIndex) => (
          <div key={group.date} className="relative">
            {/* Date separator */}
            <DateSeparator date={group.date} />

            {/* Messages in this date group */}
            {group.messages.map((message, messageIndex) => {
              const prevMessage = messageIndex > 0
                ? group.messages[messageIndex - 1]
                : groupIndex > 0
                  ? messageGroups[groupIndex - 1].messages[
                      messageGroups[groupIndex - 1].messages.length - 1
                    ]
                  : null;

              const isGrouped = canGroupMessages(prevMessage, message);
              const isOwn = currentUserId && message.senderId === currentUserId;

              // Use custom renderer if provided
              if (renderMessage) {
                return renderMessage({
                  key: message.id,
                  message,
                  isGrouped,
                  isOwn,
                  onClick: onMessageClick
                    ? () => onMessageClick(message)
                    : undefined
                });
              }

              return (
                <MessageItem
                  key={message.id}
                  message={message}
                  isGrouped={isGrouped}
                  isOwn={isOwn}
                />
              );
            })}
          </div>
        ))}

        {/* Bottom anchor for scrolling */}
        <div ref={bottomRef} className="h-1" />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <ScrollToBottomButton
          onClick={() => scrollToBottom(true)}
          newMessageCount={newMessageCount}
        />
      )}
    </div>
  );
}

// ============================================
// Named Exports
// ============================================

export {
  DateSeparator,
  MessageItem,
  LoadingIndicator,
  LoadingSkeleton,
  EmptyState,
  ErrorState,
  LoadMoreIndicator,
  ScrollToBottomButton,
  formatDateSeparator,
  formatMessageTime,
  groupMessagesByDate,
  canGroupMessages,
  SCROLL_THRESHOLD,
  INFINITE_SCROLL_THRESHOLD
};
